import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type ChangeEvent,
  type DragEvent as ReactDragEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { CloseIcon, AddIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { Button } from '@/components/ui/Button/Button';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { DropZoneOverlay } from '@/components/ui/DropZoneOverlay/DropZoneOverlay';
import { ProjectFilesPopoverFileRow } from './ProjectFilesPopoverFileItems';
import { ProjectFileRenameModal } from './ProjectFileRenameModal';
import { PROJECTS_TABLE_ICON_ACTION_WRAP_SELECTOR } from '@/components/ProjectNotesPopover/projectNotesPopoverConstants';
import { filterEntityFilesByProjectFilesTypeFilter } from './projectFilesPopoverTypeFilterModel';
import { ProjectFilesPopoverTypeFilter } from './ProjectFilesPopoverTypeFilterMenu';
import { useProjectFilesPopoverTypeFilter } from './useProjectFilesPopoverTypeFilter';
import { dataTransferHasFiles, splitFileNameStemAndSuffix } from './projectFilesPopoverUtils';
import { ENTITY_FILES_UPLOAD_STEP_TITLES, useEntityFilesManager } from './useEntityFilesManager';
import { ProjectFilesClientWorkProgress } from './ProjectFilesClientWorkProgress';
import type { EntityFilesPopoverRow, EntityFilesPopoverServerProps } from './entityFilesPopover.types';
import '@/components/ui/PopoverInlineLoading.css';

export function EntityFilesPopoverServer<T extends EntityFilesPopoverRow = EntityFilesPopoverRow>({
  open,
  onClose,
  anchorEl,
  entityTitle,
  fileLinkSelection,
  realtimeDedupProjectId,
  fileTypeMenuEnabled = false,
  typeFilterPersistence = 'localStorage',
  filesQuery,
  filesApi,
  onMutationsSettled,
}: EntityFilesPopoverServerProps<T>) {
  const queryClient = useQueryClient();
  const { showError: showToastError, showSuccess: showToastSuccess } = useToast();
  const popoverRef = useRef<HTMLDivElement>(null);
  const fileDropBodyRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryKey = filesQuery.queryKey;

  const [renameTarget, setRenameTarget] = useState<T | null>(null);
  const [fileDragHighlight, setFileDragHighlight] = useState(false);
  const osFileDragActiveRef = useRef(false);

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);

  const typeFilter = useProjectFilesPopoverTypeFilter(typeFilterPersistence);

  const {
    files: filesSorted,
    isPending: filesPending,
    isError: filesError,
    error: filesFetchError,
    uploadUi,
    isUploading,
    uploadFiles: processUploadFiles,
    deleteFile: handleDelete,
    deletingId,
    isDeleteBusy,
    patchFile: patchProjectFile,
    renameFile,
    updatingFileId,
    isUpdateBusy,
  } = useEntityFilesManager<T>({
    active: shouldRender,
    filesQuery,
    filesApi,
    realtimeDedupProjectId,
    onMutationsSettled,
  });

  useEffect(() => {
    if (!shouldRender) {
      setRenameTarget(null);
    }
  }, [shouldRender]);

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    verticalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1000,
    priorityAxis: 'x',
    clampOnScrollFollow: true,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    excludeSelector: `.context-menu, ${PROJECTS_TABLE_ICON_ACTION_WRAP_SELECTOR}`,
    /** Пока открыта модалка переименования (портал в body), клики по ней не должны считаться «вне» поповера */
    enabled: shouldRender && renameTarget === null,
    capture: false,
    shouldIgnoreOutside: () => osFileDragActiveRef.current,
  });

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && renameTarget === null) requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, requestClose, renameTarget]);

  useEffect(() => {
    if (!shouldRender) return;
    const onDragEnter = (e: DragEvent) => {
      if (dataTransferHasFiles(e.dataTransfer)) osFileDragActiveRef.current = true;
    };
    const clearOsDrag = () => {
      osFileDragActiveRef.current = false;
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('drop', clearOsDrag);
    window.addEventListener('dragend', clearOsDrag);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('drop', clearOsDrag);
      window.removeEventListener('dragend', clearOsDrag);
    };
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldRender) setFileDragHighlight(false);
  }, [shouldRender]);

  const notifyCopiedLinkSuccess = useCallback(() => {
    showToastSuccess('Ссылка скопирована');
  }, [showToastSuccess]);

  const notifyCopiedLinkError = useCallback(() => {
    showToastError('Не удалось скопировать ссылку');
  }, [showToastError]);

  const handleRequestRenameById = useCallback(
    (fileId: string) => {
      const list = queryClient.getQueryData<T[]>(queryKey);
      const f = list?.find((x) => x.file_id === fileId);
      if (f) setRenameTarget(f);
    },
    [queryClient, queryKey]
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files ?? []);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await processUploadFiles(selectedFiles);
    },
    [processUploadFiles]
  );

  const handleFilesBodyDragOver = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
    if (!dataTransferHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setFileDragHighlight(true);
  }, []);

  const handleFilesBodyDragLeave = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
    if (!dataTransferHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    if (!fileDropBodyRef.current?.contains(e.relatedTarget as Node)) {
      setFileDragHighlight(false);
    }
  }, []);

  const handleFilesBodyDrop = useCallback(
    async (e: ReactDragEvent<HTMLDivElement>) => {
      if (!dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      setFileDragHighlight(false);
      const dropped = Array.from(e.dataTransfer.files);
      await processUploadFiles(dropped);
    },
    [processUploadFiles]
  );

  const filesFiltered = useMemo(
    () => filterEntityFilesByProjectFilesTypeFilter(filesSorted, typeFilter.state),
    [filesSorted, typeFilter.state]
  );

  const popoverContent = (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      <OverlayIntrinsicSize>
        <div
          className={`project-files-popover ${contentClasses}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-files-popover-title"
        >
          <div className="project-files-popover__header">
            <h2 id="project-files-popover-title" className="project-files-popover__title">
              {fileLinkSelection ? 'Файлы для письма: ' : 'Файлы: '}
              {entityTitle}
            </h2>
            <div className="project-files-popover__header-actions">
              <ProjectFilesPopoverTypeFilter
                state={typeFilter.state}
                setTypeToggle={typeFilter.setTypeToggle}
                setUntypedToggle={typeFilter.setUntypedToggle}
                isActive={typeFilter.isActive}
              />
              <IconButton
                aria-label="Закрыть"
                onClick={requestClose}
                size="m"
                iconSize={20}
                variant="danger"
                className="project-files-popover__header-action project-files-popover__close"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
          <div
            ref={fileDropBodyRef}
            className="project-files-popover__body"
            onDragOver={handleFilesBodyDragOver}
            onDragLeave={handleFilesBodyDragLeave}
            onDrop={handleFilesBodyDrop}
          >
            <OverlayScrollArea
              className="project-files-popover__scroll"
              osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
            >
              <div className="project-files-popover__scroll-pad">
                {filesPending ? (
                  <div className="project-popover-inline-loading" aria-busy="true" aria-live="polite">
                    <span className="project-popover-inline-loading__spinner" aria-label="Загрузка файлов" />
                  </div>
                ) : filesError ? (
                  <div className="project-files-popover__empty" role="alert">
                    <p>{filesFetchError?.message || 'Не удалось загрузить файлы'}</p>
                  </div>
                ) : (
                  <ul className="project-files-popover__list">
                    {filesSorted.length === 0 ? (
                      <li className="project-files-popover__empty">
                        <i className="fa-regular fa-file-pdf project-files-popover__empty-icon" aria-hidden="true" />
                        <p>Нет прикреплённых файлов</p>
                      </li>
                    ) : filesFiltered.length === 0 ? (
                      <li className="project-files-popover__empty">
                        <i className="fa-regular fa-file-pdf project-files-popover__empty-icon" aria-hidden="true" />
                        <p>Нет файлов по выбранному фильтру</p>
                      </li>
                    ) : (
                      filesFiltered.map((file) => (
                        <ProjectFilesPopoverFileRow
                          key={file.file_id}
                          file={file}
                          onDelete={handleDelete}
                          isDeleting={deletingId === file.file_id}
                          deleteDisabled={isDeleteBusy || isUploading || updatingFileId === file.file_id}
                          fileLinkSelectionApi={fileLinkSelection}
                          onRequestRenameById={handleRequestRenameById}
                          onCopiedLinkSuccess={notifyCopiedLinkSuccess}
                          onCopiedLinkError={notifyCopiedLinkError}
                          fileTypeMenuEnabled={fileTypeMenuEnabled}
                          patchProjectFile={fileTypeMenuEnabled ? patchProjectFile : undefined}
                        />
                      ))
                    )}
                  </ul>
                )}
              </div>
            </OverlayScrollArea>
            {uploadUi ? (
              <ProjectFilesClientWorkProgress
                headline={ENTITY_FILES_UPLOAD_STEP_TITLES[uploadUi.step]}
                fileName={uploadUi.fileName}
                percent={uploadUi.step === 'metrics' ? null : uploadUi.pct}
              />
            ) : null}
            <div className="project-files-popover__add-btn-wrap">
              <Button
                type="button"
                variant="outlined"
                onClick={handleUploadClick}
                disabled={filesPending || isUploading || isDeleteBusy || isUpdateBusy}
                className="project-files-popover__add-btn"
              >
                <AddIcon size={18} />
                {isUploading ? 'Загрузка...' : 'Прикрепить файл'}
              </Button>
            </div>
            {fileDragHighlight ? <DropZoneOverlay /> : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
            aria-hidden="true"
          />
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  const renameStemSplit =
    renameTarget !== null
      ? splitFileNameStemAndSuffix(renameTarget.file_name)
      : ({ stem: '', suffix: '' } as const);

  return (
    <>
      {shouldRender ? createPortal(popoverContent, document.body) : null}
      <ProjectFileRenameModal
        open={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        initialStem={renameStemSplit.stem}
        immutableSuffix={renameStemSplit.suffix}
        submitting={renameTarget !== null && updatingFileId === renameTarget.file_id}
        onSave={async (fullFileName) => {
          if (!renameTarget) return;
          await renameFile(renameTarget.file_id, fullFileName);
        }}
      />
    </>
  );
}
